// src/components/NavBar.jsx
import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  useMediaQuery,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { scrollToSection } from "../utils/scrollToSection";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const sections = [
  { id: "about", label: "About" },
  { id: "mission", label: "Mission" },
  { id: "ai-llm", label: "AI LLM" },
  { id: "technology", label: "Technology" },
  { id: "contact", label: "Contact" },
];

const docsMenu = [
/*  { path: "/policy", label: "Policy Architecture" },
  { path: "/broker-assistant", label: "Broker Assistant" },
  { path: "/test-cases", label: "Test Cases" },*/
  { path: "/docs", label: "Documentation" },
  { path: "/ai-dashboard", label: "AI Training" },
];
const pages = [
  { label: "Docs", isDropdown: true },
  { path: "/admin", label: "Admin" },
];

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isMobile = useMediaQuery("(max-width:900px)");
  const { isAuthenticated } = useAuth();
  const [docsAnchorEl, setDocsAnchorEl] = React.useState(null);
  const handleDocsMenuOpen = (event) => setDocsAnchorEl(event.currentTarget);
  const handleDocsMenuClose = () => setDocsAnchorEl(null);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMobileMenu = () => setMobileOpen((prev) => !prev);

  // ✅ Unified handler for section buttons (works from any page)
  const handleSectionClick = (id) => {
    if (location.pathname === "/") {
      scrollToSection(id);
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
    setMobileOpen(false);
  };

  // ✅ Title click = scroll to top (from anywhere)
  const handleTitleClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: "top" } });
    }
    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(15,23,42,0.55)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Toolbar sx={{ maxWidth: 1200, mx: "auto", width: "100%" }}>
          {/* Brand / title with logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleTitleClick}>
            <Box
              component="img"
              src="/logo192.png"
              alt="Logo"
              sx={{ width: 36, height: 36, mr: 1 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              CarbonBridge.Cloud
            </Typography>
          </Box>

          {isMobile ? (
            // 🔹 Mobile: menu icon
            <Box sx={{ marginLeft: "auto" }}>
              <IconButton
                edge="end"
                color="inherit"
                onClick={toggleMobileMenu}
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            // 🔹 Desktop layout
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                ml: 4,
              }}
            >
              {/* Section actions (scrolling on home) */}
              <Box sx={{ display: "flex", gap: 1 }}>
                {sections.map((s) => (
                  <Button
                    key={s.id}
                    color="inherit"
                    sx={{ mx: 0.25 }}
                    onClick={() => handleSectionClick(s.id)}
                  >
                    {s.label}
                  </Button>
                ))}
              </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Visual separator between sections and pages */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 2, borderColor: "rgba(148,163,184,0.4)" }}
              />

              {/* Page actions (real routes) */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Docs dropdown */}
                <Button
                  color="inherit"
                  sx={{ mx: 0.25 }}
                  onClick={isAuthenticated ? handleDocsMenuOpen : undefined}
                  aria-controls={docsAnchorEl ? 'docs-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={docsAnchorEl ? 'true' : undefined}
                  disabled={!isAuthenticated}
                >
                  Docs
                </Button>
                <Menu
                  id="docs-menu"
                  anchorEl={docsAnchorEl}
                  open={Boolean(docsAnchorEl)}
                  onClose={handleDocsMenuClose}
                  MenuListProps={{ 'aria-labelledby': 'docs-button' }}
                >
                  {docsMenu.map((item) => (
                    <MenuItem
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      onClick={handleDocsMenuClose}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
                {/* Other pages */}
                <Button
                  key="admin"
                  component={RouterLink}
                  to="/admin"
                  color="inherit"
                  sx={{ mx: 0.25 }}
                >
                  Admin
                </Button>

                {/* Primary CTA – still uses scroll-to-section */}
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ ml: 1, borderRadius: 999 }}
                  onClick={() => handleSectionClick("contact")}
                >
                  Book a Demo
                </Button>
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* 🔹 Mobile dropdown menu with clear grouping */}
      {isMobile && mobileOpen && (
        <Box
          sx={{
            position: "absolute",
            top: 64,
            left: 0,
            right: 0,
            bgcolor: "rgba(15, 23, 42, 0.98)",
            borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
            zIndex: (theme) => theme.zIndex.appBar - 1,
            px: 2,
            pb: 2,
          }}
        >
          {/* Brand clickable on mobile too */}
          <Button
            fullWidth
            color="inherit"
            sx={{
              justifyContent: "flex-start",
              textAlign: "left",
              my: 0.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
            onClick={handleTitleClick}
          >
            CarbonBridge.Cloud
          </Button>

          {/* Section actions */}
          {sections.map((s) => (
            <Button
              key={s.id}
              fullWidth
              color="inherit"
              sx={{ justifyContent: "flex-start", textAlign: "left", my: 0.25 }}
              onClick={() => handleSectionClick(s.id)}
            >
              {s.label}
            </Button>
          ))}

          {/* Separator */}
          <Divider sx={{ my: 1, borderColor: "rgba(148,163,184,0.4)" }} />

          {/* Page actions */}
          {pages.map((p) => (
            <Button
              key={p.path}
              fullWidth
              component={RouterLink}
              to={p.path}
              color="inherit"
              sx={{ justifyContent: "flex-start", textAlign: "left", my: 0.25 }}
              onClick={() => setMobileOpen(false)}
            >
              {p.label}
            </Button>
          ))}
          <Button
            fullWidth
            component={RouterLink}
            to="/ai-dashboard"
            color="inherit"
            sx={{ justifyContent: "flex-start", textAlign: "left", my: 0.25 }}
            onClick={() => setMobileOpen(false)}
          >
            AI Training
          </Button>

          {/* CTA */}
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            sx={{ mt: 1, borderRadius: 999 }}
            onClick={() => handleSectionClick("contact")}
          >
            Book a Demo
          </Button>
        </Box>
      )}
    </>
  );
};

export default NavBar;
